import { useBusinessEngineApi, useSyberryTodayApi } from 'infrastructure/api';
import useFeatureToggle from 'framework/featureToggle';
import { HTTP } from 'framework/api';
import { Features } from 'infrastructure/featureToggle';
import { ListParams, ListResponse, LoadingParams } from 'framework/list';
import {
  RedmineIssueDto,
  RedmineTimeTrackDto,
  ReviewTimeTrackCommand,
  TaskManagementIssueDto,
  TaskManagementTimeEntryDto,
  TimeTrackChangeRequestDto,
} from './types';

interface TimeTracksSearchParams {
  filter: { issue_id: { eq: number } };
  pageSize: number;
  pageNumber: number;
}

interface WorkloadReportingIssuesData {
  requestType: string;
  reasonForChange: string;
  changes: any[];
}

const mapIssue = (issue: TaskManagementIssueDto): RedmineIssueDto => ({
  id: issue.id,
  subject: issue.subject,
  project: issue.project,
  status: issue.status,
  author: issue.author,
  assignedTo: issue.assignee ?? undefined,
  due_date: issue.dueDate ?? undefined,
  estimated_hours: issue.estimatedHours ?? undefined,
  pessimistic_estimated_hours: issue.pessimisticEstimatedHours ?? undefined,
});

const mapTimeEntry = (timeEntry: TaskManagementTimeEntryDto): RedmineTimeTrackDto => ({
  id: timeEntry.id,
  hours: timeEntry.hours,
  comments: timeEntry.comments ?? undefined,
  spent_on: timeEntry.spentOn,
  created_on: timeEntry.createdOn ?? undefined,
  updated_on: timeEntry.updatedOn ?? undefined,
});

const mapListResponse = <T, R>(response: ListResponse<T>, mapper: (item: T) => R): ListResponse<R> => ({
  ...response,
  result: (response.result || []).map(mapper),
});

const useBusinessEngineTimeTrackingApi = () => {
  const { sendGetRequest, sendPostRequest } = useBusinessEngineApi();

  const searchIssues = async (params: ListParams & { filter?: any }): Promise<ListResponse<RedmineIssueDto>> => {
    const transformedParams: LoadingParams = {
      pageSize: params.pageSize || 30,
      pageNumber: params.page || 0,
      filter: {
        ...params.filter,
        issue_id: params.filter?.issue_id?.ct ? { eq: params.filter.issue_id.ct } : params.filter?.issue_id,
        // Set `status_id` to `*` by default (redmine does not return closed tasks by default.)
        status_id: params.filter?.status_id ? params.filter.status_id : { eq: '*' },
      },
      sort: params.sort,
    };

    const response = await sendPostRequest('/time-tracking/issues/search', transformedParams);
    return response.json();
  };

  const searchTimeTracks = async (params: TimeTracksSearchParams): Promise<ListResponse<RedmineTimeTrackDto>> => {
    const response = await sendPostRequest('/time-tracking/time-tracks/search', params);
    return response.json();
  };

  return { sendGetRequest, sendPostRequest, searchIssues, searchTimeTracks };
};

// ITS07 against task-management, which reads Redmine data directly (behind Features.TASK_MANAGEMENT_ITS07).
const useTaskManagementTimeTrackingApi = () => {
  const { sendGetRequest, sendPostRequest } = useSyberryTodayApi();

  const searchIssues = async (params: ListParams & { filter?: any }): Promise<ListResponse<RedmineIssueDto>> => {
    const issueIdFilter = params.filter?.issue_id?.ct ? { eq: params.filter.issue_id.ct } : params.filter?.issue_id;
    const transformedParams: LoadingParams = {
      pageSize: params.pageSize || 30,
      pageNumber: params.page || 0,
      filter: issueIdFilter ? { id: issueIdFilter } : {},
      sort: params.sort,
    };

    const response = await sendPostRequest('/issues/search', transformedParams);
    return mapListResponse(await response.json(), mapIssue);
  };

  const searchTimeTracks = async (params: TimeTracksSearchParams): Promise<ListResponse<RedmineTimeTrackDto>> => {
    const response = await sendPostRequest('/time-entries/search', {
      pageSize: params.pageSize,
      pageNumber: params.pageNumber,
      filter: { issueId: params.filter.issue_id },
    });
    return mapListResponse(await response.json(), mapTimeEntry);
  };

  return { sendGetRequest, sendPostRequest, searchIssues, searchTimeTracks };
};

interface ResolvedChangeRequest {
  changeRequest: TimeTrackChangeRequestDto;
  // True when the request lives in business-engine; its review must go there as well.
  isLegacy: boolean;
}

const useTimeTrackingService = () => {
  const { isFeatureEnabled } = useFeatureToggle();
  const businessEngineApi = useBusinessEngineTimeTrackingApi();
  const taskManagementApi = useTaskManagementTimeTrackingApi();
  const isTaskManagementEnabled = isFeatureEnabled(Features.TASK_MANAGEMENT_ITS07);
  const { sendPostRequest, searchIssues, searchTimeTracks } = isTaskManagementEnabled
    ? taskManagementApi
    : businessEngineApi;

  const getIssue = async (issueId: number): Promise<RedmineIssueDto | undefined> => {
    const response = await searchIssues({
      filter: { issue_id: { eq: issueId } },
      pageSize: 1,
      page: 0,
    });
    return response.result?.[0];
  };

  const getTimeTracksForIssue = async (issueId: number): Promise<RedmineTimeTrackDto[]> => {
    const response = await searchTimeTracks({
      filter: { issue_id: { eq: issueId } },
      pageSize: 1000,
      pageNumber: 0,
    });
    return response.result || [];
  };

  const createChangeRequest = async (data: WorkloadReportingIssuesData): Promise<void> => {
    await sendPostRequest('/time-tracking/request', data);
  };

  /**
   * Change requests created before TASK_MANAGEMENT_ITS07 was enabled still live in business-engine,
   * so a task-management 404 falls back to it.
   */
  const getChangeRequest = async (requestId: string): Promise<ResolvedChangeRequest> => {
    const path = `/time-tracking/request/${requestId}`;
    const fromBusinessEngine = async (): Promise<ResolvedChangeRequest> => {
      const response = await businessEngineApi.sendGetRequest(path);
      return { changeRequest: await response.json(), isLegacy: true };
    };

    if (!isTaskManagementEnabled) {
      return fromBusinessEngine();
    }
    try {
      const response = await taskManagementApi.sendGetRequest(path);
      return { changeRequest: await response.json(), isLegacy: false };
    } catch (error: any) {
      if (error?.status !== HTTP.NOT_FOUND) {
        throw error;
      }
      return fromBusinessEngine();
    }
  };

  const reviewChangeRequest = async (data: ReviewTimeTrackCommand, isLegacy: boolean): Promise<void> => {
    const api = isLegacy ? businessEngineApi : taskManagementApi;
    await api.sendPostRequest('/time-tracking/request/review', data);
  };

  return {
    searchIssues,
    searchTimeTracks,
    getIssue,
    getTimeTracksForIssue,
    createChangeRequest,
    getChangeRequest,
    reviewChangeRequest,
  };
};

export default useTimeTrackingService;
